import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, Calendar, Users, MapPin, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { format } from 'date-fns';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const { t, language } = useLanguage();
  const [status, setStatus] = useState('loading');
  const [booking, setBooking] = useState(null);
  const [room, setRoom] = useState(null);
  const [upsells, setUpsells] = useState([]);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          // Fetch booking details from metadata
          if (response.data.metadata?.booking_id) {
            await fetchBookingDetails(response.data.metadata.booking_id);
          }
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else {
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    };

    const fetchBookingDetails = async (bookingId) => {
      try {
        const [bookingRes, upsellsRes] = await Promise.all([
          axios.get(`${API}/bookings/${bookingId}`),
          axios.get(`${API}/upsells`)
        ]);
        
        setBooking(bookingRes.data);
        
        // Fetch room details
        const roomRes = await axios.get(`${API}/rooms/${bookingRes.data.room_id}`);
        setRoom(roomRes.data);
        
        // Filter upsells
        if (bookingRes.data.upsell_ids?.length > 0) {
          const selectedUpsells = upsellsRes.data.filter(u => 
            bookingRes.data.upsell_ids.includes(u.id)
          );
          setUpsells(selectedUpsells);
        }
      } catch (error) {
        console.error('Error fetching booking details:', error);
      }
    };

    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const calculateNights = () => {
    if (!booking) return 0;
    const checkIn = new Date(booking.check_in);
    const checkOut = new Date(booking.check_out);
    return Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center" data-testid="payment-loading">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#D4AF37] mx-auto mb-4" />
          <p className="text-[#64748B] font-sans">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6 py-20" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl w-full bg-white shadow-[0_20px_40px_-15px_rgba(15,31,60,0.1)]"
      >
        {status === 'success' ? (
          <>
            {/* Header */}
            <div className="bg-[#0F1F3C] text-white p-8 text-center">
              <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-serif text-3xl mb-2">{t('booking.bookingConfirmed')}</h1>
              <p className="text-white/70 font-sans">{t('booking.thankYou')}</p>
            </div>

            {/* Booking Details */}
            <div className="p-8">
              {/* Reference */}
              {sessionId && (
                <div className="bg-[#F9F8F6] p-4 mb-6 text-center">
                  <p className="text-xs uppercase tracking-wider text-[#64748B] font-sans mb-1">
                    {t('booking.bookingReference')}
                  </p>
                  <p className="font-mono text-[#0F1F3C] font-medium">{sessionId.slice(0, 24)}...</p>
                </div>
              )}

              {/* Room Details */}
              {room && (
                <div className="border-b border-gray-100 pb-6 mb-6">
                  <div className="flex space-x-4">
                    <img 
                      src={room.images[0]} 
                      alt={room.name[language] || room.name.en}
                      className="w-24 h-24 object-cover flex-shrink-0"
                    />
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#D4AF37] font-sans mb-1">
                        {room.view}
                      </p>
                      <h3 className="font-serif text-xl text-[#0F1F3C]">
                        {room.name[language] || room.name.en}
                      </h3>
                      <p className="text-sm text-[#64748B] font-sans mt-1">
                        {room.size_sqm} m² • {room.bed_type}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Stay Details */}
              {booking && (
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <Calendar className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#64748B] font-sans">
                        {t('booking.checkIn')}
                      </p>
                      <p className="font-sans text-[#0F1F3C] font-medium">
                        {format(new Date(booking.check_in), 'EEE, MMM dd, yyyy')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Calendar className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#64748B] font-sans">
                        {t('booking.checkOut')}
                      </p>
                      <p className="font-sans text-[#0F1F3C] font-medium">
                        {format(new Date(booking.check_out), 'EEE, MMM dd, yyyy')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Users className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#64748B] font-sans">
                        {t('booking.guests')}
                      </p>
                      <p className="font-sans text-[#0F1F3C] font-medium">
                        {booking.guests} {booking.guests === 1 ? t('booking.guest') : t('booking.guests')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#64748B] font-sans">
                        Duration
                      </p>
                      <p className="font-sans text-[#0F1F3C] font-medium">
                        {calculateNights()} {calculateNights() === 1 ? t('booking.night') : t('booking.nights')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Guest Info */}
              {booking && (
                <div className="bg-[#F9F8F6] p-4 mb-6">
                  <p className="text-xs uppercase tracking-wider text-[#64748B] font-sans mb-2">
                    Guest Information
                  </p>
                  <p className="font-sans text-[#0F1F3C] font-medium">{booking.guest_name}</p>
                  <p className="text-sm text-[#64748B] font-sans">{booking.guest_email}</p>
                  <p className="text-sm text-[#64748B] font-sans">{booking.guest_phone}</p>
                </div>
              )}

              {/* Upsells */}
              {upsells.length > 0 && (
                <div className="border-t border-gray-100 pt-6 mb-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                    <p className="text-xs uppercase tracking-wider text-[#64748B] font-sans">
                      {t('booking.upsellsPrice')}
                    </p>
                  </div>
                  {upsells.map(upsell => (
                    <div key={upsell.id} className="flex justify-between items-center py-2">
                      <span className="font-sans text-[#0F1F3C]">
                        {upsell.name[language] || upsell.name.en}
                      </span>
                      <span className="font-sans text-[#0F1F3C]">€{upsell.price}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Price Summary */}
              {booking && (
                <div className="border-t border-gray-100 pt-6">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="font-sans text-[#64748B]">{t('booking.roomPrice')}</span>
                      <span className="font-sans text-[#0F1F3C]">€{booking.room_price?.toFixed(2)}</span>
                    </div>
                    {booking.upsells_price > 0 && (
                      <div className="flex justify-between">
                        <span className="font-sans text-[#64748B]">{t('booking.upsellsPrice')}</span>
                        <span className="font-sans text-[#0F1F3C]">€{booking.upsells_price?.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-4 border-t border-gray-100">
                      <span className="font-sans font-semibold text-[#0F1F3C]">{t('booking.totalPrice')}</span>
                      <span className="font-serif text-2xl text-[#0F1F3C]">€{booking.total_price?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-[#F9F8F6] p-6 text-center">
              <p className="text-sm text-[#64748B] font-sans mb-4">
                {t('booking.confirmationSent')} {booking?.guest_email}
              </p>
              <Link
                to="/"
                className="inline-block bg-[#0F1F3C] text-white hover:bg-[#1A2F4F] px-10 py-4 text-xs uppercase tracking-widest font-sans font-medium transition-all duration-300"
                data-testid="return-home-btn"
              >
                {t('booking.returnHome')}
              </Link>
            </div>
          </>
        ) : (
          <div className="p-12 text-center">
            <h1 className="font-serif text-3xl text-[#0F1F3C] mb-4">Payment Status</h1>
            <p className="text-[#64748B] font-sans mb-8">
              {status === 'expired'
                ? 'Your payment session has expired. Please try again.'
                : 'We could not verify your payment. Please contact support.'}
            </p>
            <Link
              to="/rooms"
              className="inline-block bg-[#0F1F3C] text-white hover:bg-[#1A2F4F] px-10 py-4 text-xs uppercase tracking-widest font-sans font-medium transition-all duration-300"
            >
              Try Again
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
};
